import { Option, Select } from "@deere/fuel-react";
import { Dispatch } from "react";
import { useTranslation } from "react-i18next";
import { D2DOrdersActions, setFilterAction } from "../../reducers/actions";
import { D2DOrdersFilters } from "../../types";

export enum D2DOrderStatus {
  NEW = "NEW",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED",
  SHIPPED = "SHIPPED",
  COMPLETED = "COMPLETED",
  CREDITED = "CREDITED",
  // Virtual sub-statuses used only by the status filter dropdown to
  // differentiate auto-reject ("D2D Claimed") from manual reject.
  D2D_CLAIMED = "D2D_CLAIMED",
  MANUAL_REJECTED = "MANUAL_REJECTED",
}

// Filter dropdown options include the reject sub-statuses instead of the
// generic REJECTED value.
export const D2D_ORDER_STATUS_FILTER_OPTIONS: D2DOrderStatus[] = [
  D2DOrderStatus.NEW,
  D2DOrderStatus.ACCEPTED,
  D2DOrderStatus.D2D_CLAIMED,
  D2DOrderStatus.MANUAL_REJECTED,
  D2DOrderStatus.EXPIRED,
  D2DOrderStatus.SHIPPED,
  D2DOrderStatus.COMPLETED,
  D2DOrderStatus.CREDITED,
];

type D2DOrderStatusFilterProps = {
  filters: D2DOrdersFilters;
  dispatch: Dispatch<D2DOrdersActions>;
};

const D2DOrderStatusFilter = ({
  filters,
  dispatch,
}: D2DOrderStatusFilterProps) => {
  const { t } = useTranslation("d2d");

  const getStatusLabel = (status: D2DOrderStatus) => {
    if (status === D2DOrderStatus.D2D_CLAIMED) {
      return t("status.d2dClaimed", { defaultValue: "D2D Claimed" });
    }
    if (status === D2DOrderStatus.MANUAL_REJECTED) {
      return t("status.manualRejected", { defaultValue: "Manual Reject" });
    }
    const translationKey =
      status === D2DOrderStatus.NEW ? "requested" : status.toLowerCase();
    return t(`status.${translationKey}`);
  };

  return (
    <Select
      hasSearch
      hasSelectAll
      id="d2d-order-status-dropdown"
      data-testid="d2d-order-status-dropdown"
      multiple
      onChange={(event) => {
        const { selectedOptions } = event.target;
        const values = Array.from(
          selectedOptions,
          (option) => option.value as D2DOrderStatus
        );
        dispatch(
          setFilterAction({
            ...filters,
            statuses: values,
          })
        );
      }}
      value={filters.statuses || []}
    >
      {D2D_ORDER_STATUS_FILTER_OPTIONS.map((value) => (
        <Option
          key={value}
          value={value}
          data-testid={`d2d-order-status-filter-${value}`}
        >
          {getStatusLabel(value)}
        </Option>
      ))}
    </Select>
  );
};

export default D2DOrderStatusFilter;
